import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ExternalLink } from 'lucide-react';
import { toast as siiToast } from 'sonner';
import { validarRutChileno, consultarSII } from '../lib/rut';

// Iter 291.b (Carlos, feb-2026) — mismo listado fijo que Proveedores
// (ProveedoresList.jsx::FORMAS_PAGO).
export const FORMAS_PAGO = [
  'Contado',
  'Transferencia',
  'Cheque 30 días',
  'Cheque 60 días',
  'Cheque 90 días',
  'Otro',
];

export const CLIENTE_INITIAL_FORM = {
  razon_social: '',
  rut: '',
  contacto: '',
  telefono: '',
  email: '',
  direccion: '',
  giro: '',
  comuna: '',
  ciudad: '',
  emails_secundarios: [],
  emails_cxp: [],
  envio_automatico_dte: true,
  require_oc_cliente: false,
  require_hes: false,
  require_codigo_contrato: false,
  codigo_contrato: '',
  require_centro_costo: false,
  centro_costo: '',
  codigo_interno_proveedor: '',
  condicion_pago: '',
  condicion_pago_codigo: '',
  dias_plazo: 0,
  forma_pago: '',
};

/**
 * ClienteFormFields — campos del maestro de Clientes (Iter 307.b, Carlos).
 * Componente ÚNICO compartido entre "Información > Personas > Clientes > Nuevo
 * Cliente" y el diálogo "Crear cliente nuevo" del formulario de Proyectos/OTs,
 * para que ambos pidan la MISMA información y guarden en el mismo registro.
 */
export function ClienteFormFields({ formData, setFormData, condicionesPago = [] }) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="rut">RUT</Label>
          <Input
            id="rut"
            value={formData.rut}
            onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
            placeholder="12.345.678-9"
            data-testid="input-rut"
          />
          {formData.rut && formData.rut.trim() && !validarRutChileno(formData.rut) && (
            <p className="text-xs text-red-500" data-testid="rut-error">
              Dígito verificador inválido (módulo 11)
            </p>
          )}
          {/* Iter 292.a (Carlos, feb-2026) — Link SII, mismo patrón que GenerarOCPage. */}
          <button
            type="button"
            onClick={() => consultarSII(formData.rut, siiToast)}
            className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
            data-testid="btn-consultar-sii"
          >
            <ExternalLink className="w-3 h-3" /> Consultar situación tributaria en SII
          </button>
        </div>
        <div className="space-y-2">
          <Label htmlFor="razon_social">Razón Social *</Label>
          <Input
            id="razon_social"
            value={formData.razon_social}
            onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
            placeholder="Nombre de la empresa"
            data-testid="input-razon-social"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contacto">Contacto</Label>
          <Input
            id="contacto"
            value={formData.contacto}
            onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
            placeholder="Nombre del contacto"
            data-testid="input-contacto"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="telefono">Teléfono</Label>
          <Input
            id="telefono"
            value={formData.telefono}
            onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
            placeholder="+56 9 1234 5678"
            data-testid="input-telefono"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="contacto@empresa.com"
            data-testid="input-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="direccion">Dirección</Label>
          <Input
            id="direccion"
            value={formData.direccion}
            onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
            placeholder="Dirección completa"
            data-testid="input-direccion"
          />
        </div>
        {/* Iter 415 — Datos de emisión de DTE del receptor: Giro, Comuna, Ciudad. */}
        <div className="space-y-2">
          <Label htmlFor="giro">Giro</Label>
          <Input
            id="giro"
            value={formData.giro || ''}
            onChange={(e) => setFormData({ ...formData, giro: e.target.value })}
            placeholder="Giro / actividad económica"
            data-testid="input-giro"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="comuna">Comuna</Label>
          <Input
            id="comuna"
            value={formData.comuna || ''}
            onChange={(e) => setFormData({ ...formData, comuna: e.target.value })}
            placeholder="Comuna"
            data-testid="input-comuna"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ciudad">Ciudad</Label>
          <Input
            id="ciudad"
            value={formData.ciudad || ''}
            onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
            placeholder="Ciudad"
            data-testid="input-ciudad"
          />
        </div>
        {/* Iter 291.b (Carlos, feb-2026) — mismo par Condición + Forma
            que Proveedores. La Condición viene del catálogo
            `condiciones_pago` (CP-*); los días de plazo se toman
            automáticamente. La Forma es texto libre desde FORMAS_PAGO
            (idéntica constante de ProveedoresList). */}
        <div className="space-y-2">
          <Label htmlFor="condicion_pago_codigo">Condición de Pago</Label>
          <select
            id="condicion_pago_codigo"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={formData.condicion_pago_codigo || ''}
            onChange={(e) => {
              const codigo = e.target.value;
              const cp = condicionesPago.find(c => c.codigo === codigo);
              setFormData({
                ...formData,
                condicion_pago_codigo: codigo,
                condicion_pago: cp ? cp.nombre : '',
                dias_plazo: cp ? (cp.dias_plazo || 0) : 0,
              });
            }}
            data-testid="input-condicion-pago-codigo"
          >
            <option value="">— Seleccionar —</option>
            {condicionesPago.map((cp) => (
              <option key={cp.codigo} value={cp.codigo}>
                {cp.nombre}{cp.dias_plazo > 0 ? ` (${cp.dias_plazo} días)` : ''}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Los días de plazo se toman automáticamente desde <b>Configuración &gt; Condiciones de Pago</b>.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="forma_pago">Forma de Pago</Label>
          <select
            id="forma_pago"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={formData.forma_pago || ''}
            onChange={(e) => setFormData({ ...formData, forma_pago: e.target.value })}
            data-testid="input-forma-pago"
          >
            <option value="">— Seleccionar —</option>
            {FORMAS_PAGO.map((fp) => <option key={fp} value={fp}>{fp}</option>)}
          </select>
        </div>
      </div>
      {/* Iter 291.c (Carlos, feb-2026) — Checkbox de shadcn para que se vea
          claramente marcado (fondo azul con tilde blanco). */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 pt-3 border-t">
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          data-testid="label-require-oc"
        >
          <Checkbox
            checked={!!formData.require_oc_cliente}
            onCheckedChange={(v) => setFormData({ ...formData, require_oc_cliente: !!v })}
            data-testid="input-require-oc-cliente"
          />
          <span className="text-sm">Exigir <b>N° OC del Cliente</b> al facturar</span>
        </label>
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          data-testid="label-require-hes"
        >
          <Checkbox
            checked={!!formData.require_hes}
            onCheckedChange={(v) => setFormData({ ...formData, require_hes: !!v })}
            data-testid="input-require-hes"
          />
          <span className="text-sm">Exigir <b>N° HES</b> al facturar</span>
        </label>
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          data-testid="label-require-contrato"
        >
          <Checkbox
            checked={!!formData.require_codigo_contrato}
            onCheckedChange={(v) => setFormData({ ...formData, require_codigo_contrato: !!v })}
            data-testid="input-require-contrato"
          />
          <span className="text-sm">Exigir <b>código de contrato</b> al facturar</span>
        </label>
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          data-testid="label-require-centro-costo"
        >
          <Checkbox
            checked={!!formData.require_centro_costo}
            onCheckedChange={(v) => setFormData({ ...formData, require_centro_costo: !!v })}
            data-testid="input-require-centro-costo"
          />
          <span className="text-sm">Exigir <b>centro de costo</b> al facturar</span>
        </label>
      </div>

      {/* Iter 415 — Datos de emisión: contrato / centro de costo / código interno + correos del receptor */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 pt-3 border-t">
        <div className="space-y-2">
          <Label htmlFor="codigo_contrato">Código de contrato</Label>
          <Input id="codigo_contrato" value={formData.codigo_contrato || ''}
            onChange={(e) => setFormData({ ...formData, codigo_contrato: e.target.value })}
            placeholder="(opcional)" data-testid="input-codigo-contrato" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="centro_costo">Centro de costo</Label>
          <Input id="centro_costo" value={formData.centro_costo || ''}
            onChange={(e) => setFormData({ ...formData, centro_costo: e.target.value })}
            placeholder="(opcional)" data-testid="input-centro-costo" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="codigo_interno_proveedor">Cód. interno como proveedor</Label>
          <Input id="codigo_interno_proveedor" value={formData.codigo_interno_proveedor || ''}
            onChange={(e) => setFormData({ ...formData, codigo_interno_proveedor: e.target.value })}
            placeholder="(opcional)" data-testid="input-codigo-interno" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="emails_secundarios">Correos secundarios</Label>
          <Input id="emails_secundarios" value={(formData.emails_secundarios || []).join(', ')}
            onChange={(e) => setFormData({ ...formData, emails_secundarios: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
            placeholder="correo1@x.cl, correo2@x.cl" data-testid="input-emails-secundarios" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="emails_cxp">Correos de Cuentas por Pagar</Label>
          <Input id="emails_cxp" value={(formData.emails_cxp || []).join(', ')}
            onChange={(e) => setFormData({ ...formData, emails_cxp: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
            placeholder="cxp@cliente.cl" data-testid="input-emails-cxp" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none pt-6" data-testid="label-envio-auto">
          <Checkbox
            checked={formData.envio_automatico_dte !== false}
            onCheckedChange={(v) => setFormData({ ...formData, envio_automatico_dte: !!v })}
            data-testid="input-envio-automatico"
          />
          <span className="text-sm">Enviar el DTE <b>automáticamente</b> al emitir</span>
        </label>
      </div>
    </>
  );
}
